import { apiClient } from './api';
import type {
  TrainingGround,
  TrainingScenario,
  TrainingAssignment,
  TrainingSession,
  TrainingReview,
  SkillExtractionJob,
  ApiResponse,
} from '@/types';

class TrainingService {
  // Grounds

  async getGrounds(): Promise<TrainingGround[]> {
    const response = await apiClient.get<ApiResponse<TrainingGround[]>>('/training/grounds');
    return response.data.data || [];
  }

  async getGround(id: string): Promise<TrainingGround> {
    const response = await apiClient.get<ApiResponse<TrainingGround>>(`/training/grounds/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Training ground not found');
  }

  async createGround(data: { name: string; description?: string; agent_id: string }): Promise<TrainingGround> {
    const response = await apiClient.post<ApiResponse<TrainingGround>>('/training/grounds', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create training ground');
  }

  async updateGround(id: string, data: Partial<TrainingGround>): Promise<TrainingGround> {
    const response = await apiClient.patch<ApiResponse<TrainingGround>>(`/training/grounds/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update training ground');
  }

  async deleteGround(id: string): Promise<void> {
    await apiClient.delete(`/training/grounds/${id}`);
  }

  // Scenarios

  async getScenarios(groundId: string): Promise<TrainingScenario[]> {
    const response = await apiClient.get<ApiResponse<TrainingScenario[]>>(`/training/grounds/${groundId}/scenarios`);
    return response.data.data || [];
  }

  async createScenario(groundId: string, data: { name: string; description?: string; bot_id: string; scenario_guidance: string; difficulty: string }): Promise<TrainingScenario> {
    const response = await apiClient.post<ApiResponse<TrainingScenario>>(`/training/grounds/${groundId}/scenarios`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create scenario');
  }

  async deleteScenario(groundId: string, scenarioId: string): Promise<void> {
    await apiClient.delete(`/training/grounds/${groundId}/scenarios/${scenarioId}`);
  }

  // Assignments

  async getAssignments(groundId: string): Promise<TrainingAssignment[]> {
    const response = await apiClient.get<ApiResponse<TrainingAssignment[]>>(`/training/grounds/${groundId}/assignments`);
    return response.data.data || [];
  }

  async createAssignments(groundId: string, userIds: string[]): Promise<TrainingAssignment[]> {
    const response = await apiClient.post<ApiResponse<TrainingAssignment[]>>(`/training/grounds/${groundId}/assignments`, { user_ids: userIds });
    return response.data.data || [];
  }

  async removeAssignment(groundId: string, assignmentId: string): Promise<void> {
    await apiClient.delete(`/training/grounds/${groundId}/assignments/${assignmentId}`);
  }

  // Sessions

  async getSessions(params?: { status?: string; ground_id?: string }): Promise<TrainingSession[]> {
    const response = await apiClient.get<ApiResponse<TrainingSession[]>>('/training/sessions', { params });
    return response.data.data || [];
  }

  async getSession(id: string): Promise<TrainingSession> {
    const response = await apiClient.get<ApiResponse<TrainingSession>>(`/training/sessions/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Training session not found');
  }

  async startSession(scenarioId: string): Promise<{ session: TrainingSession; first_message: string }> {
    const response = await apiClient.post<ApiResponse<{ session: TrainingSession; first_message: string }>>('/training/sessions/start', { scenario_id: scenarioId });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to start training session');
  }

  async sendMessage(sessionId: string, message: string): Promise<{ human_message: string; bot_reply: string }> {
    const response = await apiClient.post<ApiResponse<{ human_message: string; bot_reply: string }>>(`/training/sessions/${sessionId}/message`, { message });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to send message');
  }

  async completeSession(sessionId: string): Promise<{ session: TrainingSession; review: TrainingReview }> {
    const response = await apiClient.post<ApiResponse<{ session: TrainingSession; review: TrainingReview }>>(`/training/sessions/${sessionId}/complete`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to complete session');
  }

  // Session Messages

  async getSessionMessages(sessionId: string): Promise<Array<{
    id: string;
    sender_type: 'customer' | 'agent' | 'human' | 'system';
    content: string;
    created_at: string;
  }>> {
    const response = await apiClient.get<ApiResponse<Array<{
      id: string;
      sender_type: 'customer' | 'agent' | 'human' | 'system';
      content: string;
      created_at: string;
    }>>>(`/training/sessions/${sessionId}/messages`);
    return response.data.data || [];
  }

  // Reviews

  async getReviews(status?: string, groundId?: string): Promise<TrainingReview[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (groundId) params.ground_id = groundId;
    const response = await apiClient.get<ApiResponse<TrainingReview[]>>('/training/reviews', { params: Object.keys(params).length ? params : undefined });
    return response.data.data || [];
  }

  async getReview(id: string): Promise<TrainingReview> {
    const response = await apiClient.get<ApiResponse<TrainingReview>>(`/training/reviews/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Review not found');
  }

  async submitVerdict(reviewId: string, data: { status: string; score: number; feedback: string }): Promise<TrainingReview> {
    const response = await apiClient.post<ApiResponse<TrainingReview>>(`/training/reviews/${reviewId}/verdict`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to submit verdict');
  }

  async bulkApproveReviews(reviewIds: string[]): Promise<TrainingReview[]> {
    const response = await apiClient.post<ApiResponse<TrainingReview[]>>('/training/reviews/bulk-approve', { review_ids: reviewIds });
    return response.data.data || [];
  }

  async startExtraction(groundId: string): Promise<SkillExtractionJob> {
    const response = await apiClient.post<ApiResponse<SkillExtractionJob>>(`/training/grounds/${groundId}/extract`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to start extraction');
  }

  async getExtractionJobs(groundId: string): Promise<SkillExtractionJob[]> {
    const response = await apiClient.get<ApiResponse<SkillExtractionJob[]>>(`/training/grounds/${groundId}/extract-jobs`);
    return response.data.data || [];
  }
}

export const trainingService = new TrainingService();
